import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  OrderExportResponseDto,
  OrderHistoryPosResponseDto,
  OrderHistoryResponseDto,
  OrderStatusEnum,
  OrderType,
  PaymentMethod,
  PaymentStatus,
  PaymentTypeEnum,
  ProviderEnum,
  TimeSpan,
} from "api";
import { apiInstances, defaultQueryOptions } from "../config";

export type QueryParams = {
  businessIds: number[];
  timeSpan?: TimeSpan;
  start?: string;
  end?: string;
  page?: number;
  rowPerPage?: number;
  timezone?: string;
  provider?: ProviderEnum[];
  paymentType?: PaymentTypeEnum[];
  orderStatus?: OrderStatusEnum[];
  paymentStatus?: PaymentStatus[];
  orderType?: OrderType[];
  paymentTypePos?: PaymentMethod[];
};

// ✅ ONLINE ORDERS (MULTI BUSINESS)
export const useOrderHistory = (params: QueryParams, enabled = true) => {
  return useQuery<OrderHistoryResponseDto>({
    queryKey: ["orderHistory", params],
    queryFn: async () => {
      const response = await apiInstances.historyApi.getOrderHistoryOnline(
        params.businessIds, // ✅ PASS ARRAY DIRECTLY
        params.timeSpan,
        params.start,
        params.end,
        params.page,
        params.rowPerPage,
        params.timezone,
        params.provider,
        params.paymentType,
        params.orderStatus,
        params.paymentStatus,
        params.orderType,
        params.paymentTypePos
      );
      return response.data;
    },
    enabled: enabled && params.businessIds.length > 0,
    ...defaultQueryOptions,
  });
};

// Pagination is deliberately omitted: the export covers everything the filters select.
export const useExportOrderHistory = () => {
  return useMutation<OrderExportResponseDto, Error, QueryParams>({
    mutationFn: async (params) => {
      const response = await apiInstances.historyApi.exportOrderHistoryOnline(
        params.businessIds,
        params.timeSpan,
        params.start,
        params.end,
        undefined,
        undefined,
        params.timezone,
        params.provider,
        params.paymentType,
        params.orderStatus,
        params.paymentStatus,
        params.orderType,
        params.paymentTypePos
      );
      return response.data;
    },
  });
};

export const useExportPosOrderHistory = () => {
  return useMutation<OrderExportResponseDto, Error, QueryParams>({
    mutationFn: async (params) => {
      const response = await apiInstances.historyApi.exportOrderHistoryPOS(
        params.businessIds[0] ? [params.businessIds[0]] : [],
        params.timeSpan,
        params.start,
        params.end,
        undefined,
        undefined,
        params.timezone,
        params.provider,
        params.paymentType,
        params.orderStatus,
        params.paymentStatus,
        params.orderType,
        params.paymentTypePos
      );
      return response.data;
    },
  });
};

export const useOrderHistoryPOS = (params: QueryParams, enabled = true) => {
  return useQuery<OrderHistoryPosResponseDto>({
    queryKey: ["orderHistoryPOS", params],
    queryFn: async () => {
      const singleBusinessId = params.businessIds[0]
        ? [params.businessIds[0]]
        : [];

      const response = await apiInstances.historyApi.getOrderHistoryPOS(
        singleBusinessId,
        params.timeSpan,
        params.start,
        params.end,
        params.page,
        params.rowPerPage,
        params.timezone,
        params.provider,
        params.paymentType,
        params.orderStatus,
        params.paymentStatus,
        params.orderType,
        params.paymentTypePos
      );
      return response.data;
    },
    enabled: enabled && params.businessIds.length === 1,
    ...defaultQueryOptions,
  });
};
